// Client-side mutation envelope construction. Server ALWAYS re-computes
// command_sha256 from the canonical body — the client hash is advisory.

import { commandSha256, CANONICAL_VERSION } from '@edv-next/canonical-json';

export type Operation = 'insert' | 'update' | 'delete' | 'archive' | 'restore';

export interface CommandEnvelope {
  organization_id: string | null;
  site_id: string | null;
  device_id: string;
  entity_type: string;
  entity_id: string;
  operation: Operation;
  expected_version: number | null;
  payload: Record<string, unknown>;
}

export interface FullMutation extends CommandEnvelope {
  mutation_id: string;
  client_timestamp?: string;
  command_sha256?: string;
  canonical_version?: string;
}

export async function withCommandSha256(m: FullMutation): Promise<FullMutation> {
  const hash = await commandSha256({
    organization_id: m.organization_id,
    site_id: m.site_id,
    device_id: m.device_id,
    entity_type: m.entity_type,
    entity_id: m.entity_id,
    operation: m.operation,
    expected_version: m.expected_version,
    payload: m.payload,
  });
  return { ...m, command_sha256: hash, canonical_version: CANONICAL_VERSION };
}

// UUID v7 generator per RFC draft: 48-bit unix_ts_ms || rand_a(12) || rand_b(62) || variant/version.
export function generateUuidV7(
  now: number = Date.now(),
  randBytes?: (n: number) => Uint8Array,
): string {
  const rand =
    randBytes ??
    ((n: number) => {
      const b = new Uint8Array(n);
      if (typeof globalThis.crypto !== 'undefined' && globalThis.crypto.getRandomValues) {
        globalThis.crypto.getRandomValues(b);
      } else {
        for (let i = 0; i < n; i++) b[i] = Math.floor(Math.random() * 256);
      }
      return b;
    });
  const ts = BigInt(now);
  const bytes = new Uint8Array(16);
  bytes[0] = Number((ts >> 40n) & 0xffn);
  bytes[1] = Number((ts >> 32n) & 0xffn);
  bytes[2] = Number((ts >> 24n) & 0xffn);
  bytes[3] = Number((ts >> 16n) & 0xffn);
  bytes[4] = Number((ts >> 8n) & 0xffn);
  bytes[5] = Number(ts & 0xffn);
  const r = rand(10);
  for (let i = 0; i < 10; i++) bytes[6 + i] = r[i]!;
  // Set version 7 (upper nibble of byte 6)
  bytes[6] = (bytes[6]! & 0x0f) | 0x70;
  // Set variant 10 (upper two bits of byte 8)
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
